import { useCallback, useMemo, useState } from 'react';

import { ColumnConfig, TabConfig, TableSettingKey } from '../CustomersV2.types';

export type TableSettings = Record<TableSettingKey, boolean>;

/** Every toggle off. Callers get a copy, never this object. */
export const ALL_SETTINGS_OFF: Readonly<TableSettings> = {
  latestPayment: false,
  declined: false,
};

/**
 * Where a toggle sits until the seller decides for themselves: the value used
 * for a key the tab declares but storage doesn't hold. A stored `false` still
 * wins — a choice already made is not a missing one.
 */
const SETTING_DEFAULTS: Readonly<TableSettings> = {
  ...ALL_SETTINGS_OFF,
  latestPayment: true,
};

const storageKey = (config: TabConfig) =>
  `iwocapay.customersV2.tableSettings.${config.key}`;

/**
 * The tab's settings, taking only the keys it declares from `source`. Anything
 * the tab doesn't offer stays off, so neither storage nor a caller can turn on
 * a toggle with no switch behind it — and a value that isn't exactly `true`
 * reads as off rather than truthy. A key `source` doesn't hold at all takes its
 * value from `absent`.
 */
const narrow = (
  config: TabConfig,
  source: Partial<Record<string, unknown>>,
  absent: Readonly<TableSettings> = ALL_SETTINGS_OFF,
): TableSettings => {
  const settings = { ...ALL_SETTINGS_OFF };
  for (const { key } of config.settings) {
    settings[key] = key in source ? source[key] === true : absent[key];
  }
  return settings;
};

/** The toggles a tab shows a seller who has never applied settings to it. */
export const defaultTableSettings = (config: TabConfig): TableSettings =>
  narrow(config, {}, SETTING_DEFAULTS);

/**
 * Reads the persisted settings. Any failure (unavailable or corrupt storage,
 * non-object payload) falls back to the defaults and never throws.
 */
const readSettings = (config: TabConfig): TableSettings => {
  try {
    const raw = localStorage.getItem(storageKey(config));
    if (raw === null) return defaultTableSettings(config);
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return defaultTableSettings(config);
    }
    return narrow(
      config,
      parsed as Partial<Record<string, unknown>>,
      SETTING_DEFAULTS,
    );
  } catch {
    return defaultTableSettings(config);
  }
};

export function useTableSettings(config: TabConfig): {
  settings: TableSettings;
  setSettings: (next: TableSettings) => void;
  visibleColumns: ColumnConfig[];
} {
  const [settings, setSettingsState] = useState<TableSettings>(() =>
    readSettings(config),
  );

  const setSettings = useCallback(
    (next: TableSettings) => {
      const narrowed = narrow(config, next);
      setSettingsState(narrowed);
      try {
        localStorage.setItem(storageKey(config), JSON.stringify(narrowed));
      } catch {
        // Persisting is best-effort; in-memory state is still updated above.
      }
    },
    [config],
  );

  const visibleColumns = useMemo(
    () =>
      config.columns.filter(
        (column) => !column.revealedBy || settings[column.revealedBy],
      ),
    [config, settings],
  );

  return { settings, setSettings, visibleColumns };
}
